import Phaser from 'phaser';
import { GAME_WIDTH } from '../config';

export const Theme = {
  colors: {
    bg: { primary: 0xfef0f5, secondary: 0xfef7e6, dark: 0x1a0f2e },
    pink: { main: 0xff6fa1, dark: 0xe54e87, light: 0xffd6e6, bg: 0xfff5f8 },
    gold: { main: 0xffc94c, dark: 0xf0a020, light: 0xfff8d8 },
    purple: { main: 0x9b6bff, dark: 0x6f3acf, light: 0xece0ff },
    mint: { main: 0x6fd4c4, dark: 0x4ab5a5, light: 0xc0ebe0 },
    text: { dark: 0x3a2540, mid: 0x7a5b80, light: 0xb89cc0 },
    rarity: {
      common: 0xcccccc,
      rare: 0x6fd4c4,
      epic: 0x9b6bff,
      legend: 0xffc94c,
      season: 0xff6fa1,
    },
  },
  hex: {
    text: '#3a2540',
    textMid: '#7a5b80',
    textLight: '#b89cc0',
    pink: '#ff6fa1',
    pinkDark: '#e54e87',
    gold: '#ffc94c',
    goldDark: '#f0a020',
    purple: '#9b6bff',
    purpleDark: '#6f3acf',
    mint: '#6fd4c4',
    white: '#ffffff',
  },
  font: {
    family: 'Pretendard Variable, Pretendard, -apple-system, sans-serif',
  },
  radius: { card: 18, button: 12, chip: 999, small: 8 },
  spacing: { xs: 4, sm: 8, md: 16, lg: 24, xl: 40 },
  shadow: { color: 0x3a2540, alpha: 0.12, offsetX: 3, offsetY: 5 },
};

// Helper: draw a polished card with shadow
export function drawCard(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  opts?: {
    fill?: number;
    border?: number;
    borderWidth?: number;
    radius?: number;
    shadowAlpha?: number;
  }
): void {
  const fill = opts?.fill ?? 0xffffff;
  const border = opts?.border ?? Theme.colors.pink.light;
  const bw = opts?.borderWidth ?? 2;
  const r = opts?.radius ?? Theme.radius.card;
  const sAlpha = opts?.shadowAlpha ?? Theme.shadow.alpha;

  // Drop shadow
  g.fillStyle(Theme.shadow.color, sAlpha);
  g.fillRoundedRect(
    x + Theme.shadow.offsetX,
    y + Theme.shadow.offsetY,
    w,
    h,
    r
  );
  // Card background
  g.fillStyle(fill, 1);
  g.fillRoundedRect(x, y, w, h, r);
  // Border
  g.lineStyle(bw, border, 1);
  g.strokeRoundedRect(x, y, w, h, r);
}

// Helper: page header rendered at consistent y positions
export function drawPageHeader(
  scene: Phaser.Scene,
  title: string,
  subtitle?: string
): void {
  scene.add.text(GAME_WIDTH / 2, 70, title, {
    fontFamily: Theme.font.family,
    fontSize: '34px',
    fontStyle: '800',
    color: Theme.hex.text,
  }).setOrigin(0.5);

  if (subtitle) {
    scene.add.text(GAME_WIDTH / 2, 120, subtitle, {
      fontFamily: Theme.font.family,
      fontSize: '16px',
      color: Theme.hex.textMid,
    }).setOrigin(0.5);
  }
}

// Helper: a small section-label chip (pill background with text)
export function drawChip(
  scene: Phaser.Scene,
  x: number,
  y: number,
  text: string,
  fillColor: number = Theme.colors.pink.bg,
  textColor: string = Theme.hex.pink
): void {
  const label = scene.add.text(x, y, text, {
    fontFamily: Theme.font.family,
    fontSize: '13px',
    fontStyle: '700',
    color: textColor,
  }).setOrigin(0.5);
  const bg = scene.add.graphics();
  bg.fillStyle(fillColor, 1);
  bg.fillRoundedRect(
    label.x - label.width / 2 - 10,
    label.y - label.height / 2 - 4,
    label.width + 20,
    label.height + 8,
    Theme.radius.chip
  );
  // Bring label to front by re-adding
  bg.setDepth(label.depth - 1);
  label.setDepth(bg.depth + 1);
}

// Helper: stagger-fade-in animation for an array of GameObjects
export function staggerFadeIn(
  scene: Phaser.Scene,
  objects: Phaser.GameObjects.GameObject[],
  startDelay = 0,
  staggerMs = 40
): void {
  objects.forEach((obj, i) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const go = obj as any;
    if (typeof go.setAlpha !== 'function') return;
    go.setAlpha(0);
    scene.tweens.add({
      targets: obj,
      alpha: 1,
      duration: 200,
      delay: startDelay + i * staggerMs,
      ease: 'Sine.Out',
    });
  });
}
